/* Analýza V2+ — kontextová pole podle typu smlouvy.
 *
 * PROČ TO EXISTUJE (nález Owner UAT):
 * Všechny kategorie zdrojů měly jednu plochou sadu polí. U stavebního spoření
 * pak chyběl konec vázací doby i cílová částka (bez nich se nedá říct, jestli
 * se vyplatí pokračovat), zatímco u běžného účtu svítilo „Datum založení“
 * a „Splatnost“, které tam nikdo nikdy nevyplní.
 *
 * Sada polí je proto součástí TYPU produktu, ne formuláře.
 *
 * POZOR — stejná past jako u model.js: musí to být FUNKCE beroucí měnu
 * parametrem. Konstanta vyhodnocená při importu by navždy zamrzla na „Kč“
 * a slovenský poradce by v kartách viděl koruny.
 */

import { MENY } from "./meny";

/* Kategorie zdrojů, pro které máme vlastní sadu polí.
 * Klíče odpovídají model.js → EMPTY.majetek. */
export const KATEGORIE = ["stavebko", "penzijko", "opf", "kzp", "ucty"];

export const KATEGORIE_LABEL = {
  cs: {
    stavebko: "Stavební spoření",
    penzijko: "Penzijní produkty",
    opf: "Investice",
    kzp: "Životní pojištění s hodnotou",
    ucty: "Účty a hotovost",
  },
  sk: {
    stavebko: "Stavebné sporenie",
    penzijko: "Dôchodkové produkty",
    opf: "Investície",
    kzp: "Životné poistenie s hodnotou",
    ucty: "Účty a hotovosť",
  },
  /* en/uk/vi doplněno 5. 10. 2026 (velká kontrola, T1-07) — dřív spadly do
   * češtiny („Účty a hotovost“ na vietnamské obrazovce). */
  en: {
    stavebko: "Building savings",
    penzijko: "Pension products",
    opf: "Investments",
    kzp: "Life insurance with a cash value",
    ucty: "Accounts and cash",
  },
  uk: {
    stavebko: "Будівельні заощадження",
    penzijko: "Пенсійні продукти",
    opf: "Інвестиції",
    kzp: "Страхування життя з накопиченням",
    ucty: "Рахунки та готівка",
  },
  vi: {
    stavebko: "Tiết kiệm nhà ở",
    penzijko: "Sản phẩm hưu trí",
    opf: "Đầu tư",
    kzp: "Bảo hiểm nhân thọ có giá trị tích lũy",
    ucty: "Tài khoản và tiền mặt",
  },
};

/* Nabídky do výběrových polí. Jsou to KATEGORIE, ne doporučení — poradce
 * jen zapisuje, co klient má. */
export const STRATEGIE = ["Konzervativní", "Vyvážená", "Dynamická", "Neznámá"];
export const TYPY_INVESTIC = [
  "Podílový fond",
  "ETF",
  "Jednotlivé akcie",
  "Dluhopisy",
  "Nemovitostní fond",
  "Komodity / zlato",
  "Kryptoměny",
];
export const TYPY_UCTU = ["Běžný účet", "Spořicí účet", "Termínovaný vklad", "Hotovost", "Cizí měna"];

/* Druh penzijního produktu. Sekce „2. Penzijní fondy" sbírá tři různé smlouvy
 * naráz a poradci to teleprompter i diktuje („staré penzijko, DPS, DIP").
 * Bez tohohle pole je nemá aplikace jak rozlišit — a rozlišit je MUSÍ:
 *   • DPS a staré připojištění smí ze zákona vést jen penzijní společnost,
 *   • DIP smí vést i banka, obchodník s CP a investiční společnost,
 *   • státní příspěvek (20 % z 500–1700 Kč) náleží DPS, DIP ne.
 * Hodnoty (`v`) jsou klíče, popisky (`l`) text pro poradce — klíč se nesmí
 * měnit, čte ho lib/analyzaV2/instituceProAnalyzu.js (DRUH_NA_TYP).
 * Prázdno je legitimní stav „poradce zatím neurčil", ne nula. */
export const DRUHY_PENZIJKA = [
  { v: "pripojisteni", l: "Staré penzijní připojištění" },
  { v: "dps", l: "Doplňkové penzijní spoření (DPS)" },
  { v: "dip", l: "Dlouhodobý investiční produkt (DIP)" },
];

/* ── Stavebnice polí ──────────────────────────────────────────────
 * Každé pole má { k, l, type } a volitelně { suffix, wide, list, moznosti }.
 * type: "text" | "number" | "date" | "check" | "select"
 */
const text = (k, l, extra = {}) => ({ k, l, type: "text", ...extra });
const cislo = (k, l, suffix, extra = {}) => ({ k, l, type: "number", suffix, ...extra });
const datum = (k, l) => ({ k, l, type: "date" });
const vyber = (k, l, moznosti) => ({ k, l, type: "select", moznosti });
const zaskrtnout = (k, l) => ({ k, l, type: "check" });

const instituce = () => text("instituce", "Instituce", { list: "av-instituce" });
const cisloSmlouvy = () => text("cislo", "Číslo smlouvy");
const poleMeny = () => vyber("mena", "Měna", MENY);
const poznamka = () => text("pozn", "Poznámka", { wide: true });

/**
 * Sada polí pro danou kategorii zdroje.
 *
 * `cur` je měnový symbol z useLanguage().currency.symbol (Kč / €) — používá se
 * jen jako popisek za číslem. Skutečná měna položky je pole `mena`.
 *
 * Neznámá kategorie dostane základní sadu, ne prázdný formulář: poradce musí
 * mít kam zapsat i produkt, který jsme nepředvídali.
 */
export function polePodleTypu(kategorie, cur) {
  const c = String(cur ?? "").trim();
  const mesicne = c ? `${c}/m` : "";

  switch (String(kategorie ?? "").trim()) {
    // Stavebko stojí a padá s vázací dobou a cílovou částkou — bez nich
    // se nedá poradit, jestli pokračovat, nebo vybrat.
    case "stavebko":
      return [
        instituce(),
        cisloSmlouvy(),
        datum("zalozeno", "Datum založení"),
        datum("konecVazaci", "Konec vázací doby"),
        cislo("cilovaCastka", "Cílová částka", c),
        cislo("urok", "Úroková sazba", "%"),
        cislo("hodnota", "Současná hodnota", c),
        cislo("vklad", "Pravidelný vklad", mesicne),
        poleMeny(),
        poznamka(),
      ];

    // U penzijka rozhoduje příspěvek zaměstnavatele a strategie — to jsou
    // dvě věci, které se u jiných produktů nevyskytují.
    case "penzijko":
      return [
        instituce(),
        // Druh rozhoduje, koho našeptávač u instituce nabídne (DPS = jen
        // penzijní společnost, DIP i banku/OCP) a jestli se řádek počítá do
        // státního příspěvku. Do řádku tabulky se nevejde (majitel: „nesmí
        // tam být moc políček"), takže sedí v bublině ⋯ — a to jako první.
        vyber("druh", "Druh produktu", DRUHY_PENZIJKA),
        cisloSmlouvy(),
        datum("zalozeno", "Datum založení"),
        datum("vyroci", "Výročí smlouvy"),
        cislo("hodnota", "Současná hodnota", c),
        cislo("vklad", "Vlastní vklad", mesicne),
        // KLÍČ MUSÍ BÝT "zam", ne "zamestnavatel". Čte ho lib/bilance.js:39
        // (S(m.penzijko, "zam")) a stejný klíč mají i všechna dřív uložená data.
        // Přejmenování klíče tady = poradce vyplní příspěvek a měsíční odkládání
        // se nezmění o korunu. Změřeno na 1 500 Kč/měs.
        cislo("zam", "Příspěvek zaměstnavatele", mesicne),
        vyber("strategie", "Strategie", STRATEGIE),
        poleMeny(),
        poznamka(),
      ];

    case "opf":
      return [
        instituce(),
        cisloSmlouvy(),
        vyber("typ", "Typ investice", TYPY_INVESTIC),
        vyber("strategie", "Strategie", STRATEGIE),
        datum("zalozeno", "Datum založení"),
        cislo("hodnota", "Současná hodnota", c),
        cislo("vklad", "Pravidelný vklad", mesicne),
        poleMeny(),
        poznamka(),
      ];

    // Kapitálové / investiční životní: platí se pojistné, ne vklad, a datum
    // počátku rozhoduje o daňovém režimu.
    case "kzp":
      return [
        instituce(),
        cisloSmlouvy(),
        datum("pocatek", "Datum počátku"),
        datum("vyroci", "Výročí smlouvy"),
        cislo("hodnota", "Odkupné / současná hodnota", c),
        // Totéž: bilance.js:40 čte S(m.kzp, "vklad"). Popisek smí být
        // „Měsíční platba" (u KŽP se platí pojistné, ne vklad), ale KLÍČ ne.
        cislo("vklad", "Měsíční platba", mesicne),
        poleMeny(),
        poznamka(),
      ];

    // Účet nemá „datum založení“ ani „splatnost“ — má typ a otázku,
    // jestli jsou peníze opravdu hned k dispozici (rezerva).
    case "ucty":
      return [
        instituce(),
        vyber("typUctu", "Typ účtu", TYPY_UCTU),
        cislo("hodnota", "Zůstatek", c),
        // Compact přechod tohle pole omylem zahodil úplně — přitom „kolik si
        // klient měsíčně posílá na spořicí účet" je jedna z nejčastějších
        // položek a bilance.js:40 ji počítá (S(m.ucty, "vklad")).
        cislo("vklad", "Posílá měsíčně", mesicne),
        /* ÚROK CHYBĚL, PŘESTOŽE HO DATOVÝ MODEL MÁ.
           `model.js` zakládá u každého účtu `urok: 0`, ale v sadě polí pro
           tuhle kategorii nebyl — takže ho nešlo nikde zadat, ani v bublině
           „Další informace". Poradce u spořicího účtu s 200 000 Kč neměl kam
           napsat, kolik nese, a analýza s tím proto nemohla počítat.
           Patří do bubliny, ne do řádku: vyplňuje se míň často než zůstatek. */
        cislo("urok", "Úrok", "%"),
        zaskrtnout("likvidni", "Okamžitě dostupné"),
        poleMeny(),
        poznamka(),
      ];

    default:
      return [instituce(), cislo("hodnota", "Současná hodnota", c), cislo("vklad", "Pravidelný vklad", mesicne), poleMeny(), poznamka()];
  }
}

/** Známe pro tuto kategorii vlastní sadu polí? (jinak se použije základní) */
export const znamaKategorie = (kategorie) => KATEGORIE.includes(String(kategorie ?? "").trim());

/** Klíče polí kategorie — pro kontrolu úplnosti a testy. */
export const kliceProTyp = (kategorie, cur = "Kč") => polePodleTypu(kategorie, cur).map((p) => p.k);

/** Má kategorie tohle pole? */
export const maPole = (kategorie, klic, cur = "Kč") => kliceProTyp(kategorie, cur).includes(klic);

export function kategorieLabel(kategorie, lang = "cs") {
  const t = KATEGORIE_LABEL[lang] || KATEGORIE_LABEL.cs;
  return t[String(kategorie ?? "").trim()] || "";
}
